import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Download, Calendar, BarChart3, PieChart as PieChartIcon,
  TrendingUp, FileSpreadsheet, ChevronDown, ChevronRight, Scissors, Edit2, X, Printer, Loader2
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts'
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns'
import { it } from 'date-fns/locale'
import api from '../services/api'
import { useWorkspace } from '../hooks/useWorkspace'
import { useTheme } from '../hooks/useTheme'
import { useLanguage } from '../hooks/useLanguage'
import CategoryIcon from '../components/CategoryIcon'
import { StatementAnalysisCard } from '../components/FinancialIntelligencePanel'
import { useFinancialStatementAnalysis } from '../hooks/useFinancialIntelligence'
import TourGuide, { TourStep } from '../components/TourGuide'
import {
  buildStatementRequest,
  createStatementKpi,
  type RetrievalSnippet,
} from '../services/financialIntelligence'

interface Category {
  id: string
  name: string
  icon: string
  color: string
  cost_type: string
}

interface CostDetailTransaction {
  id: string
  date: string
  description: string
  amount: number
}

interface CostDetailCategory {
  category_id: string
  name: string
  color: string
  cost_type: string
  total: number
  transactions: CostDetailTransaction[]
}

interface CostDetailsData {
  fixed: {
    categories: CostDetailCategory[]
    total: number
    count: number
  }
  variable: {
    categories: CostDetailCategory[]
    total: number
    count: number
  }
}

export default function Reports() {
  const { currentWorkspace } = useWorkspace()
  const { isDark } = useTheme()
  const { t, language } = useLanguage()
  const queryClient = useQueryClient()
  
  // Chart axis colors based on theme
  const axisColor = isDark ? '#9ca3af' : '#6b7280'
  
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(subMonths(new Date(), 5)), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  })
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})
  const [showCostDetails, setShowCostDetails] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<CostDetailTransaction | null>(null)

  // Fetch categories for edit modal (include workspace ID to reload when workspace changes)
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/categories/')
      return response.data as Category[]
    },
    enabled: !!currentWorkspace?.id
  })
  const categories = Array.isArray(categoriesData) ? categoriesData : []

  // Mutation to update transaction category
  const updateMutation = useMutation({
    mutationFn: async ({ id, category_id, cost_type }: { id: string; category_id: string; cost_type: string }) => {
      await api.patch(`/transactions/${id}`, { category_id, cost_type })
    },
    onSuccess: () => {
      setEditingTransaction(null)
      queryClient.invalidateQueries({ queryKey: ['cost-details'] })
      queryClient.invalidateQueries({ queryKey: ['fixed-vs-variable'] })
      queryClient.invalidateQueries({ queryKey: ['categories-breakdown'] })
    }
  })

  const handleCategoryChange = (category: Category) => {
    if (!editingTransaction) return
    updateMutation.mutate({
      id: editingTransaction.id,
      category_id: category.id,
      cost_type: category.cost_type
    })
  }

  // Fixed vs Variable over time - uses date range
  const { data: fixedVsVariableData, isLoading: loadingFixedVar, isFetching: fetchingFixedVar } = useQuery<{ data: Array<{ month: string; label: string; fixed: number; variable: number }> }>({
    queryKey: ['fixed-vs-variable', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/fixed-vs-variable?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      return response.data
    },
    retry: 1
  })
  const fixedVsVariable = useMemo(
    () => fixedVsVariableData?.data || [],
    [fixedVsVariableData?.data],
  )

  // Categories breakdown - uses date range
  const { data: categoriesBreakdownData, isLoading: loadingCategories, isFetching: fetchingCategories } = useQuery<{ data: Array<{ category_id: string; category_name: string; icon: string; color: string; cost_type: string; total: number; transaction_count: number; percentage: number }> }>({
    queryKey: ['categories-breakdown', dateRange],
    queryFn: async () => {
      const response = await api.get(
        `/reports/categories-breakdown?start_date=${dateRange.start}&end_date=${dateRange.end}`
      )
      return response.data
    },
    retry: 1
  })
  const categoriesBreakdown = useMemo(
    () => categoriesBreakdownData?.data || [],
    [categoriesBreakdownData?.data],
  )

  // Timeline - uses date range
  const { data: timelineData, isLoading: loadingTimeline, isFetching: fetchingTimeline } = useQuery<{ data: Array<{ month: string; label: string; income: number; expenses: number }> }>({
    queryKey: ['timeline-report', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/timeline?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      return response.data
    },
    retry: 1
  })
  const timeline = useMemo(() => timelineData?.data || [], [timelineData?.data])

  // Cost Details - detailed transactions grouped by cost type
  const { data: costDetailsData } = useQuery<CostDetailsData>({
    queryKey: ['cost-details', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/cost-details?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      return response.data
    },
    retry: 1,
    enabled: showCostDetails
  })

  const isInitialLoading = loadingFixedVar || loadingCategories || loadingTimeline
  const isRefetching = fetchingFixedVar || fetchingCategories || fetchingTimeline

  const statementPayload = useMemo(() => {
    if (!currentWorkspace?.name || timeline.length === 0) return null

    const totalIncome = timeline.reduce((sum, item) => sum + item.income, 0)
    const totalExpenses = timeline.reduce((sum, item) => sum + item.expenses, 0)
    const latestMonth = timeline[timeline.length - 1]
    const previousMonth = timeline[timeline.length - 2]
    const totalFixed = fixedVsVariable.reduce((sum, item) => sum + item.fixed, 0)
    const totalVariable = fixedVsVariable.reduce((sum, item) => sum + item.variable, 0)
    const topCategory = categoriesBreakdown[0]

    const executiveKpis = [
      createStatementKpi('Entrate periodo', totalIncome),
      createStatementKpi('Uscite periodo', totalExpenses),
      createStatementKpi('Saldo netto periodo', totalIncome - totalExpenses),
      createStatementKpi(
        'Uscite ultimo mese',
        latestMonth?.expenses ?? totalExpenses,
        previousMonth?.expenses,
      ),
    ]

    const technicalKpis = [
      createStatementKpi('Costi fissi periodo', totalFixed),
      createStatementKpi('Costi variabili periodo', totalVariable),
      createStatementKpi(
        'Concentrazione top categoria',
        topCategory?.percentage ?? 0,
        undefined,
        '%',
      ),
      createStatementKpi('Categorie monitorate', categoriesBreakdown.length, undefined, 'count'),
    ]

    const alertFlags = [
      ...(totalExpenses > totalIncome ? ['periodo chiuso con saldo negativo'] : []),
      ...(topCategory && topCategory.percentage >= 35
        ? [`concentrazione spesa elevata su ${topCategory.category_name}`]
        : []),
      ...(totalFixed > totalVariable ? ['incidenza dei costi fissi superiore ai variabili'] : []),
      ...(latestMonth && previousMonth && latestMonth.expenses > previousMonth.expenses * 1.15
        ? ['incremento delle uscite ultimo mese oltre il 15%']
        : []),
    ]

    const retrievalContext: RetrievalSnippet[] = [
      ...categoriesBreakdown.slice(0, 3).map((category, index) => ({
        snippet_id: `category-${index}`,
        source: 'reports/categories-breakdown',
        content: `${category.category_name}: ${category.percentage.toFixed(1)}% del totale, ${category.transaction_count} transazioni.`,
        score: 0.82,
      })),
      ...timeline.slice(-2).map((period, index) => ({
        snippet_id: `timeline-${index}`,
        source: 'reports/timeline',
        content: `${period.label}: entrate ${period.income.toFixed(2)}, uscite ${period.expenses.toFixed(2)}.`,
        score: 0.76,
      })),
    ]

    return buildStatementRequest({
      companyName: currentWorkspace.name,
      periodLabel: `${dateRange.start} - ${dateRange.end}`,
      executiveKpis,
      technicalKpis,
      alertFlags,
      retrievalContext,
    })
  }, [
    categoriesBreakdown,
    currentWorkspace?.name,
    dateRange.end,
    dateRange.start,
    fixedVsVariable,
    timeline,
  ])

  const statementAnalysisQuery = useFinancialStatementAnalysis({
    enabled: !!currentWorkspace?.id,
    payload: statementPayload,
  })

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      const response = await api.post('/reports/export', {
        format,
        start_date: dateRange.start,
        end_date: dateRange.end,
        include_categories: true,
        include_cost_types: true
      }, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `transazioni.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

  // Tour steps for Reports
  const tourSteps: TourStep[] = language === 'it' ? [
    {
      title: 'Benvenuto nei Report! 📈',
      content: 'Questa sezione ti permette di analizzare le tue spese nel dettaglio con grafici e tabelle.',
      position: 'center'
    },
    {
      target: '[data-tour="date-selector"]',
      title: 'Seleziona Periodo',
      content: 'Scegli il periodo da analizzare. Puoi selezionare mesi specifici o un intervallo personalizzato.',
      position: 'bottom'
    },
    {
      target: '[data-tour="category-report"]',
      title: 'Report per Categoria',
      content: 'Vedi come sono distribuite le spese per ogni categoria. Clicca per espandere e vedere le transazioni.',
      position: 'top'
    },
    {
      target: '[data-tour="export-buttons"]',
      title: 'Esporta i Dati',
      content: 'Esporta i report in CSV o Excel per ulteriori analisi o per archivio.',
      position: 'bottom'
    }
  ] : [
    {
      title: 'Welcome to Reports! 📈',
      content: 'This section allows you to analyze your expenses in detail with charts and tables.',
      position: 'center'
    },
    {
      target: '[data-tour="date-selector"]',
      title: 'Select Period',
      content: 'Choose the period to analyze. You can select specific months or a custom range.',
      position: 'bottom'
    },
    {
      target: '[data-tour="category-report"]',
      title: 'Category Report',
      content: 'See how expenses are distributed by category. Click to expand and see transactions.',
      position: 'top'
    },
    {
      target: '[data-tour="export-buttons"]',
      title: 'Export Data',
      content: 'Export reports to CSV or Excel for further analysis or archiving.',
      position: 'bottom'
    }
  ]

  return (
    <>
      <TourGuide tourId="reports" steps={tourSteps} />
      <div className="space-y-6 pt-1">

        {/* Full-page loading spinner on initial load */}
        {isInitialLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}

        {/* Refetch overlay indicator */}
        {isRefetching && !isInitialLoading && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg shadow-md text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            {language === 'it' ? 'Aggiornamento dati...' : 'Updating data...'}
          </div>
        )}

        {/* Header */}
      <div className={`flex items-center justify-between ${isInitialLoading ? 'hidden' : ''}`}>
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t.nav.reports}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">{t.reports.analysis}</p>
        </div>

        {/* Export and Print buttons */}
        <div data-tour="export-buttons" className="flex gap-2 no-print">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Stampa
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </button>
        </div>
      </div>

      {/* Date Range */}
      <div data-tour="date-selector" className="card">
        <div className="flex items-center gap-4 flex-wrap">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <span className="text-gray-400">—</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <StatementAnalysisCard
        data={statementAnalysisQuery.data}
        isLoading={statementAnalysisQuery.isLoading}
        isFetching={statementAnalysisQuery.isFetching}
        error={statementAnalysisQuery.error}
        onRetry={() => {
          void statementAnalysisQuery.refetch()
        }}
        subtitle="Lettura AI dei KPI aggregati del periodo selezionato, costruita sui report già presenti nella pagina."
      />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fixed vs Variable Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-gray-400" />
              Costi Fissi vs Variabili
            </h3>
          </div>
          {fixedVsVariable && fixedVsVariable.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={fixedVsVariable}>
                <XAxis 
                  dataKey="month" 
                  tickFormatter={(v) => {
                    const [, month] = v.split('-')
                    return months[parseInt(month) - 1]
                  }}
                  tick={{ fill: axisColor }}
                  stroke={axisColor}
                />
                <YAxis tickFormatter={(v) => `€${v / 1000}k`} tick={{ fill: axisColor }} stroke={axisColor} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value)}
                  labelFormatter={(label) => {
                    const [year, month] = label.split('-')
                    return `${months[parseInt(month) - 1]} ${year}`
                  }}
                />
                <Bar dataKey="fixed" name="Costi Fissi" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="variable" name="Costi Variabili" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
              Nessun dato disponibile
            </div>
          )}
          
          {/* Summary */}
          {fixedVsVariable && fixedVsVariable.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Media Costi Fissi</p>
                  <p className="text-sm font-semibold text-blue-600">
                    {formatCurrency(
                      fixedVsVariable.reduce((acc, m) => acc + m.fixed, 0) / fixedVsVariable.length
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Media Costi Variabili</p>
                  <p className="text-sm font-semibold text-amber-600">
                    {formatCurrency(
                      fixedVsVariable.reduce((acc, m) => acc + m.variable, 0) / fixedVsVariable.length
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Categories Pie Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-gray-400" />
              Distribuzione per Categoria
            </h3>
          </div>
          {categoriesBreakdown && categoriesBreakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categoriesBreakdown.slice(0, 8)}
                    dataKey="total"
                    nameKey="category_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ percentage }) => 
                      percentage > 5 ? `${percentage.toFixed(0)}%` : ''
                    }
                    labelLine={false}
                  >
                    {categoriesBreakdown.slice(0, 8).map((cat, index: number) => (
                      <Cell key={index} fill={cat.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                {categoriesBreakdown.slice(0, 8).map((cat) => (
                  <div key={cat.category_id} className="flex items-center gap-2 text-sm">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color }}
                    />
                    <CategoryIcon icon={cat.icon} className="w-4 h-4 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="text-gray-600 dark:text-gray-300 truncate">
                      {cat.category_name}
                    </span>
                    <span className="ml-auto text-gray-900 dark:text-white font-medium">
                      {formatCurrency(cat.total)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
              Nessun dato disponibile
            </div>
          )}
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-400" />
            Andamento Annuale
          </h3>
        </div>
        {timeline && timeline.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={timeline}>
              <XAxis 
                dataKey="month" 
                tickFormatter={(v) => {
                  const [, month] = v.split('-')
                  return months[parseInt(month) - 1]
                }}
                tick={{ fill: axisColor }}
                stroke={axisColor}
              />
              <YAxis tickFormatter={(v) => `€${v / 1000}k`} tick={{ fill: axisColor }} stroke={axisColor} />
              <Tooltip 
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(label) => {
                  const [year, month] = label.split('-')
                  return `${months[parseInt(month) - 1]} ${year}`
                }}
              />
              <Bar dataKey="income" name="Entrate" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Uscite" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            Nessun dato disponibile
          </div>
        )}
      </div>

      {/* Categories Table */}
      {categoriesBreakdown && categoriesBreakdown.length > 0 && (
        <div data-tour="category-report" className="card">
          <div className="card-header">
            <h3 className="card-title">Dettaglio Categorie</h3>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th className="text-right">Totale</th>
                  <th className="text-right">%</th>
                  <th className="text-right">Transazioni</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {categoriesBreakdown.map((cat) => (
                  <tr key={cat.category_id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <CategoryIcon icon={cat.icon} className="w-4 h-4 flex-shrink-0" style={{ color: cat.color }} />
                        <span className="font-medium dark:text-white">{cat.category_name}</span>
                      </div>
                    </td>
                    <td className="text-right font-medium dark:text-white">
                      {formatCurrency(cat.total)}
                    </td>
                    <td className="text-right dark:text-gray-300">
                      {cat.percentage.toFixed(1)}%
                    </td>
                    <td className="text-right text-gray-500 dark:text-gray-400">
                      {cat.transaction_count}
                    </td>
                    <td>
                      {cat.cost_type && (
                        <span className={`badge ${
                          cat.cost_type === 'fixed' ? 'badge-fixed' : 'badge-variable'
                        }`}>
                          {cat.cost_type === 'fixed' ? 'Fisso' : 'Variabile'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost Analysis Section */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Scissors className="w-5 h-5 text-gray-400" />
            Analisi Costi: Cosa Posso Tagliare?
          </h3>
          <button
            onClick={() => setShowCostDetails(!showCostDetails)}
            className={`btn ${showCostDetails ? 'btn-primary' : 'btn-secondary'}`}
          >
            {showCostDetails ? 'Nascondi Dettagli' : 'Mostra Dettagli'}
          </button>
        </div>

        {!showCostDetails ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <Scissors className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-4" />
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Clicca su "Mostra Dettagli" per vedere tutte le tue spese organizzate.</p>
            <p className="text-sm text-slate-400 max-w-[360px] text-center">Identifica quali costi fissi e variabili puoi ridurre o eliminare.</p>
          </div>
        ) : costDetailsData ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-700 dark:text-blue-300 font-semibold">Costi Fissi</span>
                  <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 px-2 py-0.5 rounded-full">
                    {costDetailsData.fixed.count} transazioni
                  </span>
                </div>
                <p className="kpi-value kpi-animate text-blue-800 dark:text-blue-200">{formatCurrency(costDetailsData.fixed.total)}</p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Difficili da ridurre (affitto, utenze, abbonamenti...)
                </p>
              </div>
              <div className="bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-cyan-700 dark:text-cyan-300 font-semibold">Costi Variabili</span>
                  <span className="text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/50 px-2 py-0.5 rounded-full">
                    {costDetailsData.variable.count} transazioni
                  </span>
                </div>
                <p className="kpi-value kpi-animate text-cyan-800 dark:text-cyan-200">{formatCurrency(costDetailsData.variable.total)}</p>
                <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-1">
                  Più facili da ottimizzare (acquisti, ristoranti...)
                </p>
              </div>
            </div>

            {/* Fixed Costs Detail */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                Costi Fissi per Categoria
              </h4>
              <div className="space-y-2">
                {costDetailsData.fixed.categories.map((cat) => (
                  <div key={cat.category_id} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(`fixed-${cat.category_id}`)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expandedCategories[`fixed-${cat.category_id}`] ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-medium text-gray-900 dark:text-white">{cat.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          ({cat.transactions.length} transazioni)
                        </span>
                      </div>
                      <span className="font-semibold text-blue-600">{formatCurrency(cat.total)}</span>
                    </button>
                    
                    {expandedCategories[`fixed-${cat.category_id}`] && (
                      <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700 p-4">
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {cat.transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between py-2 px-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-600">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 dark:text-white truncate">{tx.description || 'Nessuna descrizione'}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(tx.date).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className="text-sm font-medium text-red-600">
                                  {formatCurrency(tx.amount)}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingTransaction(tx); }}
                                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                  title="Modifica categoria"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {costDetailsData.fixed.categories.length === 0 && (
                  <p className="text-gray-500 text-center py-4">Nessun costo fisso nel periodo selezionato</p>
                )}
              </div>
            </div>

            {/* Variable Costs Detail */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                Costi Variabili per Categoria
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(qui puoi risparmiare!)</span>
              </h4>
              <div className="space-y-2">
                {costDetailsData.variable.categories.map((cat) => (
                  <div key={cat.category_id} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(`variable-${cat.category_id}`)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expandedCategories[`variable-${cat.category_id}`] ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-medium text-gray-900 dark:text-white">{cat.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          ({cat.transactions.length} transazioni)
                        </span>
                      </div>
                      <span className="font-semibold text-cyan-600">{formatCurrency(cat.total)}</span>
                    </button>
                    
                    {expandedCategories[`variable-${cat.category_id}`] && (
                      <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-4">
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {cat.transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between py-2 px-3 bg-white dark:bg-slate-700 rounded-lg border border-gray-100 dark:border-slate-600">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-900 dark:text-white truncate">{tx.description || 'Nessuna descrizione'}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(tx.date).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className="text-sm font-medium text-red-600">
                                  {formatCurrency(tx.amount)}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingTransaction(tx); }}
                                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors"
                                  title="Modifica categoria"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {costDetailsData.variable.categories.length === 0 && (
                  <p className="text-gray-500 text-center py-4">Nessun costo variabile nel periodo selezionato</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-500 mt-3">Caricamento dettagli costi...</p>
          </div>
        )}
      </div>

      {/* Edit Category Modal */}
      {editingTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal w-full max-w-lg mx-4 overflow-hidden animate-scaleIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Modifica Categoria</h3>
              <button 
                onClick={() => setEditingTransaction(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Transaction Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  {format(new Date(editingTransaction.date), 'd MMM yyyy', { locale: it })}
                </p>
                <p className="text-gray-900 dark:text-white font-medium mb-2">{editingTransaction.description}</p>
                <p className="text-sm font-bold text-red-600">
                  {formatCurrency(editingTransaction.amount)}
                </p>
              </div>

              {/* Category Selection */}
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Seleziona nuova categoria:</p>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {categories.filter(c => c.id !== 'altro' && c.id !== 'other').map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category)}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all text-left"
                    style={{ borderLeftColor: category.color, borderLeftWidth: '4px' }}
                  >
                    <CategoryIcon icon={category.icon} className="w-5 h-5" style={{ color: category.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{category.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {category.cost_type === 'fixed' ? 'Fisso' : category.cost_type === 'variable' ? 'Variabile' : 'Entrata'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {updateMutation.isPending && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Aggiornamento...</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600">
              <button
                onClick={() => setEditingTransaction(null)}
                className="w-full px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-500 font-medium"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}
